/** Simple group polls — WhatsApp-parity lite (no MLS). */

export interface PollOption {
  id: string;
  text: string;
  voterDevices: string[];
}

export interface PollRecord {
  pollId: string;
  conversationId: string;
  question: string;
  options: PollOption[];
  createdAt: number;
  closedAt?: number;
  allowMulti: boolean;
}

export function createPoll(input: {
  conversationId: string;
  question: string;
  optionTexts: string[];
  allowMulti?: boolean;
}): PollRecord {
  const options = input.optionTexts
    .map((t) => t.trim())
    .filter(Boolean)
    .slice(0, 12)
    .map((text) => ({
      id: randomId(8),
      text,
      voterDevices: [] as string[],
    }));
  if (options.length < 2) throw new Error("Poll needs at least 2 options");
  return {
    pollId: randomId(16),
    conversationId: input.conversationId,
    question: input.question.trim() || "Poll",
    options,
    createdAt: Date.now(),
    allowMulti: !!input.allowMulti,
  };
}

export function castVote(
  poll: PollRecord,
  optionId: string,
  voterDevice: string,
): PollRecord {
  if (poll.closedAt) return poll;
  const options = poll.options.map((o) => {
    let voters = o.voterDevices.filter((d) => d !== voterDevice);
    if (o.id === optionId) {
      if (!poll.allowMulti) {
        // exclusive: only this option keeps the voter
        voters = [...voters, voterDevice];
      } else if (!o.voterDevices.includes(voterDevice)) {
        voters = [...o.voterDevices, voterDevice];
      } else {
        voters = o.voterDevices.filter((d) => d !== voterDevice);
      }
    } else if (!poll.allowMulti) {
      voters = o.voterDevices.filter((d) => d !== voterDevice);
    } else {
      voters = o.voterDevices;
    }
    return { ...o, voterDevices: voters };
  });
  return { ...poll, options };
}

/**
 * Apply only `voter`'s choices from a received snapshot onto our copy (matched by option
 * id). A snapshot is authoritative about its sender's vote and nothing else, so a peer
 * can't move other people's votes, and concurrent voters no longer overwrite each other.
 */
export function mergePollVoter(local: PollRecord, snapshot: PollRecord, voter: string): PollRecord {
  const chosen = new Set(snapshot.options.filter((o) => o.voterDevices.includes(voter)).map((o) => o.id));
  return {
    ...local,
    options: local.options.map((o) => {
      const others = o.voterDevices.filter((d) => d !== voter);
      return { ...o, voterDevices: chosen.has(o.id) ? [...others, voter] : others };
    }),
  };
}

export function closePoll(poll: PollRecord, now = Date.now()): PollRecord {
  return { ...poll, closedAt: now };
}

export function encodePollWire(poll: PollRecord): string {
  return JSON.stringify({ op: "poll", poll });
}

export function decodePollWire(body: string): PollRecord | null {
  try {
    const raw = JSON.parse(body) as { op?: string; poll?: PollRecord };
    if (raw?.op !== "poll" || !raw.poll?.pollId) return null;
    return raw.poll;
  } catch {
    return null;
  }
}

function randomId(bytes: number): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return [...a].map((b) => b.toString(16).padStart(2, "0")).join("");
}
